import React, { useState, useEffect, useRef } from 'react'
import Grid from '@mui/material/Grid'
import TextField from '@/v2/components/common/TextField'
import SelectCalendar from '@/v2/components/common/SelectCalendar'
import Divider from '@/v2/components/common/Divider'
import Typography from '@/v2/components/common/Typography'
import { format } from 'date-fns'
import { SaveOutlined, CancelOutlined, EditOutlined } from '@mui/icons-material'
import Button from '@/v2/components/common/Button'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { useUpdateClaimInfo } from '@/v2/hooks/insurance/useClaims'

const Treatment = ({
  values = {
    dateAppliancePlaced: '',
    treatmentMonths: '',
    remainingMonths: '',
  },
  onChange,
  insuranceId,
  claimId,
  patientId,
  viewOnly = false,
  defaultApplianceDate = '',
  justCreated = false,
}) => {
  const theme = useTheme()
  const [isEdit, setIsEdit] = useState(
    !values.dateAppliancePlaced ||
      !values.treatmentMonths ||
      !values.remainingMonths
  )
  const [currentValues, setCurrentValues] = useState({
    dateAppliancePlaced: '',
    treatmentMonths: '',
    remainingMonths: '',
  })
  // Tracks whether the user has manually edited the appliance date, so the
  // items-start-date default never clobbers an explicit choice.
  const applianceTouched = useRef(false)
  // Opens the section in edit mode once for a brand-new claim, so the
  // auto-filled appliance date is clearly pending and saveable.
  const openedForCreate = useRef(false)
  const { updateClaim, isLoading } = useUpdateClaimInfo(
    insuranceId,
    claimId,
    patientId
  )

  useEffect(() => {
    setCurrentValues(prev => {
      const stored = values.dateAppliancePlaced || ''
      const createdDate = values.createdAt
        ? String(values.createdAt).slice(0, 10)
        : ''
      // The server stamps the appliance date with the creation date by default.
      // Treat that (or a blank) as "not yet chosen" so it can mirror the item
      // date; a date that differs is a deliberate choice we must not overwrite.
      const isAutoDefault = justCreated || !stored || stored === createdDate

      let dateAppliancePlaced
      if (applianceTouched.current) {
        // Respect a value the user explicitly entered this session
        dateAppliancePlaced = prev.dateAppliancePlaced
      } else if (!viewOnly && isAutoDefault && defaultApplianceDate) {
        // Default/track the appliance date to the items start date
        dateAppliancePlaced = defaultApplianceDate
      } else {
        dateAppliancePlaced = stored || defaultApplianceDate
      }
      return {
        dateAppliancePlaced: dateAppliancePlaced || '',
        treatmentMonths: values.treatmentMonths || '',
        remainingMonths: values.remainingMonths || '',
      }
    })
    setIsEdit(prevEdit => {
      if (justCreated && !openedForCreate.current) {
        openedForCreate.current = true
        return true
      }
      // For a brand-new claim, don't fight the user's edit state on refetches
      if (justCreated) return prevEdit
      return (
        !values.dateAppliancePlaced ||
        !values.treatmentMonths ||
        !values.remainingMonths
      )
    })
  }, [values, defaultApplianceDate, justCreated])

  const handleSave = async () => {
    try {
      await updateClaim({
        body: {
          claim: {
            dateAppliancePlaced: currentValues.dateAppliancePlaced,
            treatmentMonths: currentValues.treatmentMonths,
            remainingMonths: currentValues.remainingMonths,
          },
        },
      })
      onChange(currentValues)
      setIsEdit(false)
    } catch (error) {
      console.error('Failed to update treatment:', error)
    }
  }

  const handleCancel = () => {
    applianceTouched.current = false
    setCurrentValues({
      dateAppliancePlaced:
        values.dateAppliancePlaced || defaultApplianceDate || '',
      treatmentMonths: values.treatmentMonths || '',
      remainingMonths: values.remainingMonths || '',
    })
    setIsEdit(false)
  }

  return (
    <>
      <Grid item xs={12} sx={{ marginTop: '12px' }}>
        <Typography variant="h5" bold fontSize="16px">
          Treatment
        </Typography>
        <Divider margin="5px 0px" />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <SelectCalendar
              outlineColor={
                isEdit || !isLoading
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main
              }
              label="Date appliance placed"
              isBasicCalendar
              value={currentValues.dateAppliancePlaced}
              onChange={value => {
                applianceTouched.current = true
                setCurrentValues({
                  ...currentValues,
                  dateAppliancePlaced: value
                    ? format(new Date(value), 'yyyy-MM-dd')
                    : null,
                })
              }}
              disabled={!isEdit || isLoading}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              outlineColor={
                isEdit || !isLoading
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main
              }
              fullWidth
              name="treatmentMonths"
              label="Months of treatment"
              placeholder="Enter months"
              value={currentValues.treatmentMonths}
              onChange={value =>
                setCurrentValues({ ...currentValues, treatmentMonths: value })
              }
              disabled={!isEdit || isLoading}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              outlineColor={
                isEdit || !isLoading
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main
              }
              fullWidth
              name="remainingMonths"
              label="Remaining months"
              placeholder="Enter remaining months"
              value={currentValues.remainingMonths}
              onChange={value =>
                setCurrentValues({ ...currentValues, remainingMonths: value })
              }
              disabled={!isEdit || isLoading}
            />
          </Grid>
          {!viewOnly && (
            <Grid item xs={12}>
              <Box display="flex" flexDirection="row" gap="8px">
                {isEdit ? (
                  <>
                    <Button
                      variant="text"
                      disableElevation
                      startIcon={<SaveOutlined color="link" />}
                      onClick={handleSave}
                      disabled={isLoading}
                      sx={{
                        marginBottom: '4px',
                        color: '#000',
                        '&:hover': {
                          backgroundColor: theme.palette.silver.dark,
                        },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="text"
                      disableElevation
                      startIcon={<CancelOutlined color="error" />}
                      onClick={handleCancel}
                      disabled={isLoading}
                      sx={{
                        marginBottom: '4px',
                        color: '#000',
                        '&:hover': {
                          backgroundColor: theme.palette.silver.dark,
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="text"
                    disableElevation
                    startIcon={<EditOutlined color="link" />}
                    onClick={() => setIsEdit(true)}
                    sx={{
                      marginBottom: '4px',
                      color: '#000',
                      '&:hover': {
                        backgroundColor: theme.palette.silver.dark,
                      },
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  )
}

export default Treatment
